import React, { useEffect, useMemo, useRef, useState } from "react"
import { api } from "./api"
import { subscribeToRoomChannel } from "./cable"
import { STORAGE_KEY } from "./constants"
import { CallStage } from "./components/CallStage"
import { CameraPreview, MediaToggle } from "./components/CameraPreview"
import { EffectsIcon, MicIcon, VideoIcon, YanoLogo, YanoWordmark } from "./components/Icons"
import { UsersIcon, LinkIcon, LockClosedIcon, SparklesIcon, ShieldCheckIcon } from "@heroicons/react/24/outline"
import { useI18n, LocalePicker } from "./i18n"
import { mergeUserIntoMessages, mergeUserIntoRoom } from "./userState"
import { VIDEO_GRADES, SOFT_FILTER, filterStringForVideoEffects, normalizeVideoEffects } from "./videoEffects"

const EMPTY_FORM = {
  display_name: "",
  gender: "",
  bio: ""
}

const GENDER_VALUES = ["woman", "man", "unspecified"]
const AVATAR_ACCEPT = "image/png,image/jpeg,image/webp,image/gif"
const AVATAR_ALLOWED_TYPES = ["image/png", "image/jpeg", "image/webp", "image/gif"]
const AVATAR_MAX_BYTES = 5 * 1024 * 1024
const AVATAR_MIN_DIMENSION = 96
const AVATAR_CROP_PREVIEW_SIZE = 176
const AVATAR_ZOOM_MIN = 1
const AVATAR_ZOOM_MAX = 3

function clamp(value, min, max) {
  return Math.min(Math.max(value, min), max)
}

function buildInitialAvatarCrop(width, height) {
  const size = Math.min(width, height)
  return {
    x: Math.max(Math.round((width - size) / 2), 0),
    y: Math.max(Math.round((height - size) / 2), 0),
    size
  }
}

function avatarZoomValue(selection) {
  const minSide = Math.min(selection.width, selection.height)
  return clamp(minSide / selection.size, AVATAR_ZOOM_MIN, AVATAR_ZOOM_MAX)
}

function applyAvatarZoom(selection, zoom) {
  const minSide = Math.min(selection.width, selection.height)
  const nextZoom = clamp(Number(zoom), AVATAR_ZOOM_MIN, AVATAR_ZOOM_MAX)
  const nextSize = clamp(minSide / nextZoom, minSide / AVATAR_ZOOM_MAX, minSide)
  const centerX = selection.x + (selection.size / 2)
  const centerY = selection.y + (selection.size / 2)

  return {
    ...selection,
    size: nextSize,
    x: clamp(centerX - (nextSize / 2), 0, selection.width - nextSize),
    y: clamp(centerY - (nextSize / 2), 0, selection.height - nextSize)
  }
}

function avatarCropPayload(selection) {
  return {
    x: Number((selection.x / selection.width).toFixed(6)),
    y: Number((selection.y / selection.height).toFixed(6)),
    width: Number((selection.size / selection.width).toFixed(6)),
    height: Number((selection.size / selection.height).toFixed(6))
  }
}

function avatarPreviewStyle(selection, frameSize = AVATAR_CROP_PREVIEW_SIZE) {
  const scale = frameSize / selection.size
  return {
    width: `${selection.width * scale}px`,
    height: `${selection.height * scale}px`,
    transform: `translate(${-selection.x * scale}px, ${-selection.y * scale}px)`
  }
}

function avatarDraftSignature(selection) {
  if (!selection) return ""
  return [
    selection.previewUrl,
    selection.x.toFixed(2),
    selection.y.toFixed(2),
    selection.size.toFixed(2)
  ].join(":")
}

function loadImageDimensions(source) {
  return new Promise((resolve, reject) => {
    const image = new window.Image()
    image.onload = () => resolve({
      width: image.naturalWidth,
      height: image.naturalHeight
    })
    image.onerror = () => reject(new Error("image_load_failed"))
    image.src = source
  })
}

function avatarHueFromName(name) {
  const value = (name || "").trim()
  if (!value) return 320
  let hash = 0
  for (let i = 0; i < value.length; i += 1) {
    hash = (hash * 31 + value.charCodeAt(i)) | 0
  }
  return Math.abs(hash) % 360
}

function avatarInitial(name) {
  const value = (name || "").trim()
  if (!value) return "?"
  const codePoint = value.codePointAt(0)
  return String.fromCodePoint(codePoint).toUpperCase()
}

function ProfileGenderIcon({ value }) {
  if (value === "woman") {
    return (
      <svg viewBox="0 0 20 20" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <circle cx="10" cy="7" r="3.2" />
        <path d="M10 10.4v6.1M7.2 13.7H12.8" />
      </svg>
    )
  }

  if (value === "man") {
    return (
      <svg viewBox="0 0 20 20" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <circle cx="7.2" cy="12.8" r="3.2" />
        <path d="M9.8 10.2L15.2 4.8M11.4 4.8H15.2V8.6" />
      </svg>
    )
  }

  return (
    <svg viewBox="0 0 20 20" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="10" cy="10" r="6" />
      <path d="M7.6 12.4c.7.7 1.5 1 2.4 1s1.7-.3 2.4-1M8 8.1h.01M12 8.1h.01" />
    </svg>
  )
}

function mergeMessages(existing, incoming, prepend = false) {
  const merged = prepend ? [ ...incoming, ...existing ] : [ ...existing, ...incoming ]
  const byId = new Map()

  merged.forEach((message) => {
    byId.set(message.id, message)
  })

  return Array.from(byId.values()).sort((left, right) => left.id - right.id)
}

function currentRoomCodeFromPath() {
  const match = window.location.pathname.match(/^\/rooms\/([A-Za-z0-9_-]+)$/)
  return match?.[1]?.toUpperCase() || ""
}

function setRoomPath(code) {
  const nextPath = code ? `/rooms/${code}` : "/"
  if (window.location.pathname !== nextPath) {
    window.history.pushState({}, "", nextPath)
  }
}

const PREJOIN_GRADES = VIDEO_GRADES
const PREJOIN_SOFT_FILTER = SOFT_FILTER

function normalizeProfile(user = {}) {
  return {
    display_name: user.display_name || "",
    gender: user.gender || "",
    bio: user.bio || ""
  }
}

function loadStoredSession() {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY)
    return raw ? JSON.parse(raw) : null
  } catch (_error) {
    return null
  }
}

function saveStoredSession(session) {
  if (!session) {
    window.localStorage.removeItem(STORAGE_KEY)
    return
  }

  window.localStorage.setItem(STORAGE_KEY, JSON.stringify(session))
}

function ProfileAvatar({ name, avatarUrl, size = 44 }) {
  if (avatarUrl) {
    return (
      <img
        src={avatarUrl}
        alt=""
        className="shrink-0 rounded-full object-cover shadow-[0_6px_16px_rgba(10,4,24,0.45),inset_0_1px_0_rgba(255,255,255,0.45)]"
        style={{ width: size, height: size }}
      />
    )
  }

  const initial = avatarInitial(name)
  const hue = avatarHueFromName(name)
  const style = {
    width: size,
    height: size,
    background: `linear-gradient(140deg, oklch(0.78 0.16 ${hue}), oklch(0.66 0.18 ${(hue + 38) % 360}))`,
    fontSize: Math.round(size * 0.42)
  }

  return (
    <span
      className="font-display inline-flex shrink-0 items-center justify-center rounded-full font-extrabold text-[#1a0e2e] shadow-[0_6px_16px_rgba(10,4,24,0.45),inset_0_1px_0_rgba(255,255,255,0.45)]"
      style={style}
      aria-hidden="true"
    >
      {initial}
    </span>
  )
}

function ProfileSheet({
  open,
  initialProfile,
  avatarUrl,
  hasExistingSession,
  saving,
  errorMessage,
  onSave,
  onAvatarUpload,
  onAvatarCropUpdate,
  onAvatarRemove,
  onClose,
  onClearError
}) {
  const { t } = useI18n()
  const [draft, setDraft] = useState(initialProfile)
  const [avatarDraft, setAvatarDraft] = useState(null)
  const [avatarError, setAvatarError] = useState("")
  const [avatarSaving, setAvatarSaving] = useState(false)
  const [avatarRemoving, setAvatarRemoving] = useState(false)
  const [avatarDragging, setAvatarDragging] = useState(false)
  const wasOpenRef = useRef(false)
  const dialogRef = useRef(null)
  const nameInputRef = useRef(null)
  const fileInputRef = useRef(null)
  const avatarDragRef = useRef(null)

  useEffect(() => {
    if (open && !wasOpenRef.current) {
      setDraft(initialProfile)
      setAvatarDraft(null)
      setAvatarError("")
      setAvatarSaving(false)
      setAvatarRemoving(false)
      setAvatarDragging(false)
    }
    wasOpenRef.current = open
  }, [open, initialProfile])

  useEffect(() => {
    const previewUrl = avatarDraft?.previewUrl
    return () => {
      if (previewUrl) {
        window.URL.revokeObjectURL(previewUrl)
      }
    }
  }, [avatarDraft?.previewUrl])

  useEffect(() => {
    if (!open) return
    const previouslyFocused = document.activeElement
    const id = window.requestAnimationFrame(() => {
      nameInputRef.current?.focus({ preventScroll: true })
    })

    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = "hidden"

    function handleKey(event) {
      if (event.key !== "Escape" || saving || avatarSaving || avatarRemoving) return

      event.preventDefault()
      if (avatarDraft) clearAvatarDraft()
      else onClose()
    }

    window.addEventListener("keydown", handleKey)
    return () => {
      window.cancelAnimationFrame(id)
      window.removeEventListener("keydown", handleKey)
      document.body.style.overflow = previousOverflow
      if (previouslyFocused && typeof previouslyFocused.focus === "function") {
        try { previouslyFocused.focus({ preventScroll: true }) } catch (_e) {}
      }
    }
  }, [open, avatarDraft, hasExistingSession, saving, avatarSaving, avatarRemoving, onClose])

  if (!open) return null

  const trimmedName = draft.display_name.trim()
  const busy = saving || avatarSaving || avatarRemoving
  const canSave = Boolean(trimmedName) && !busy
  const bioLength = draft.bio.length
  const canManageAvatar = !busy

  function updateField(field, value) {
    if (errorMessage) onClearError()
    setDraft((current) => ({ ...current, [field]: value }))
  }

  function handleBackdropClick() {
    if (busy) return
    if (avatarDraft) {
      clearAvatarDraft()
      return
    }
    onClose()
  }

  async function handleSubmit(event) {
    event.preventDefault()
    if (!canSave) return
    const stagedAvatar = !hasExistingSession && avatarDraft ? avatarDraft : null
    await onSave({
      display_name: trimmedName,
      gender: draft.gender,
      bio: draft.bio.trim()
    }, stagedAvatar)
  }

  function openFilePicker() {
    if (!canManageAvatar) return
    setAvatarError("")
    if (avatarDraft && !avatarDraft.editing) {
      setAvatarDraft((current) => current ? { ...current, editing: true } : current)
      return
    }
    fileInputRef.current?.click()
  }

  async function handleAvatarFileChange(event) {
    const file = event.target.files?.[0]
    event.target.value = ""
    if (!file) return

    if (!AVATAR_ALLOWED_TYPES.includes(file.type)) {
      setAvatarError(t("profile.error.avatar_invalid_type"))
      return
    }

    if (file.size > AVATAR_MAX_BYTES) {
      setAvatarError(t("profile.error.avatar_too_large"))
      return
    }

    const previewUrl = window.URL.createObjectURL(file)

    try {
      const { width, height } = await loadImageDimensions(previewUrl)

      if (Math.min(width, height) < AVATAR_MIN_DIMENSION) {
        throw new Error("avatar_too_small")
      }

      const crop = buildInitialAvatarCrop(width, height)
      const nextDraft = {
        file,
        previewUrl,
        width,
        height,
        editing: true,
        ...crop
      }
      setAvatarDraft(nextDraft)
      setAvatarError("")
    } catch (uploadError) {
      window.URL.revokeObjectURL(previewUrl)
      setAvatarError(
        uploadError?.message === "avatar_too_small"
          ? t("profile.error.avatar_too_small")
          : t("profile.error.avatar_load_failed")
      )
    }
  }

  function updateAvatarZoom(value) {
    setAvatarDraft((current) => {
      if (!current) return current
      const next = applyAvatarZoom(current, value)
      if (next.size === current.size && next.x === current.x && next.y === current.y) return current
      return { ...next, hasChanges: true }
    })
    setAvatarError("")
  }

  function clearAvatarDraft() {
    setAvatarDraft(null)
    setAvatarError("")
    setAvatarDragging(false)
    avatarDragRef.current = null
  }

  function handleAvatarCropPointerDown(event) {
    if (!avatarDraft || busy) return
    event.preventDefault()
    event.currentTarget.setPointerCapture?.(event.pointerId)
    avatarDragRef.current = {
      pointerId: event.pointerId,
      clientX: event.clientX,
      clientY: event.clientY,
      cropX: avatarDraft.x,
      cropY: avatarDraft.y
    }
    setAvatarDragging(true)
    setAvatarError("")
  }

  function handleAvatarCropPointerMove(event) {
    if (!avatarDraft || !avatarDragRef.current) return
    if (avatarDragRef.current.pointerId !== event.pointerId) return

    event.preventDefault()
    const dragState = avatarDragRef.current
    const scale = AVATAR_CROP_PREVIEW_SIZE / avatarDraft.size
    const deltaX = (event.clientX - dragState.clientX) / scale
    const deltaY = (event.clientY - dragState.clientY) / scale

    setAvatarDraft((current) => {
      if (!current) return current

      const nextX = clamp(dragState.cropX - deltaX, 0, current.width - current.size)
      const nextY = clamp(dragState.cropY - deltaY, 0, current.height - current.size)
      if (nextX === current.x && nextY === current.y) return current

      return {
        ...current,
        x: nextX,
        y: nextY,
        hasChanges: true
      }
    })
  }

  function handleAvatarCropPointerUp(event) {
    if (avatarDragRef.current?.pointerId !== event.pointerId) return
    event.currentTarget.releasePointerCapture?.(event.pointerId)
    avatarDragRef.current = null
    setAvatarDragging(false)
  }

  async function handleUploadAvatar(nextDraft = avatarDraft) {
    if (!nextDraft || !hasExistingSession) return false

    setAvatarSaving(true)
    setAvatarError("")

    try {
      await onAvatarUpload(
        nextDraft.file,
        avatarCropPayload(nextDraft),
        { width: nextDraft.width, height: nextDraft.height }
      )
      return true
    } catch (uploadError) {
      setAvatarError(uploadError?.message || t("profile.error.avatar_failed"))
      return false
    } finally {
      setAvatarSaving(false)
    }
  }

  async function handleUpdateAvatarCrop(nextDraft = avatarDraft) {
    if (!nextDraft || !nextDraft.uploaded || !hasExistingSession) return false

    setAvatarSaving(true)
    setAvatarError("")

    try {
      await onAvatarCropUpdate(avatarCropPayload(nextDraft))
      return true
    } catch (uploadError) {
      setAvatarError(uploadError?.message || t("profile.error.avatar_failed"))
      return false
    } finally {
      setAvatarSaving(false)
    }
  }

  async function handleSaveAvatarDraft() {
    if (!avatarDraft) return

    if (!hasExistingSession) {
      setAvatarDraft((current) => current ? { ...current, editing: false, hasChanges: false } : current)
      return
    }

    const saved = avatarDraft.uploaded
      ? await handleUpdateAvatarCrop(avatarDraft)
      : await handleUploadAvatar(avatarDraft)

    if (saved) clearAvatarDraft()
  }

  async function handleRemoveAvatar() {
    if (!hasExistingSession) return

    setAvatarRemoving(true)
    setAvatarError("")

    try {
      await onAvatarRemove()
      setAvatarDraft(null)
    } catch (uploadError) {
      setAvatarError(uploadError?.message || t("profile.error.avatar_remove_failed"))
    } finally {
      setAvatarRemoving(false)
    }
  }

  function renderPhotoStage() {
    if (avatarDraft?.editing) {
      return (
        <div
          className={`yano-profile-photo__crop ${avatarDragging ? "yano-profile-photo__crop--dragging" : ""}`}
          onPointerDown={handleAvatarCropPointerDown}
          onPointerMove={handleAvatarCropPointerMove}
          onPointerUp={handleAvatarCropPointerUp}
          onPointerCancel={handleAvatarCropPointerUp}
        >
          <img
            src={avatarDraft.previewUrl}
            alt=""
            className="yano-profile-photo__image"
            style={avatarPreviewStyle(avatarDraft)}
            draggable="false"
            onDragStart={(event) => event.preventDefault()}
          />
          <span className="yano-profile-photo__crop-mask" aria-hidden="true" />
        </div>
      )
    }

    if (avatarDraft) {
      return (
        <span className="yano-profile-photo__inner yano-profile-photo__inner--staged">
          <img
            className="yano-profile-photo__image"
            src={avatarDraft.previewUrl}
            alt=""
            style={avatarPreviewStyle(avatarDraft, 140)}
            draggable="false"
          />
          <span className="yano-profile-photo__plus" aria-hidden="true">
            <svg viewBox="0 0 14 14" className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round">
              <path d="M7 2.5v9M2.5 7h9" />
            </svg>
          </span>
        </span>
      )
    }

    if (avatarUrl) {
      return (
        <span className="yano-profile-photo__inner yano-profile-photo__inner--image">
          <img className="yano-profile-photo__image" src={avatarUrl} alt="" />
          <span className="yano-profile-photo__plus" aria-hidden="true">
            <svg viewBox="0 0 14 14" className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round">
              <path d="M7 2.5v9M2.5 7h9" />
            </svg>
          </span>
        </span>
      )
    }

    return (
      <>
        <span className="yano-profile-photo__ring" />
        <span className="yano-profile-photo__aura" />
        <span className="yano-profile-photo__inner">
          <svg viewBox="0 0 28 28" className="h-8 w-8" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
            <path d="M5 9.2h3.2l1.6-2.2h8.4l1.6 2.2H23a1.6 1.6 0 0 1 1.6 1.6v9.4a1.6 1.6 0 0 1-1.6 1.6H5a1.6 1.6 0 0 1-1.6-1.6v-9.4A1.6 1.6 0 0 1 5 9.2Z" />
            <circle cx="14" cy="15.2" r="3.6" />
          </svg>
          <span className="yano-profile-photo__plus" aria-hidden="true">
            <svg viewBox="0 0 14 14" className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round">
              <path d="M7 2.5v9M2.5 7h9" />
            </svg>
          </span>
        </span>
      </>
    )
  }

  const genderOptions = [
    { value: "woman", label: t("profile.gender.woman") },
    { value: "man", label: t("profile.gender.man") },
    { value: "unspecified", label: t("profile.gender.unspecified") }
  ]

  return (
    <div className="fixed inset-0 z-[400] flex items-end justify-center sm:items-center sm:p-6">
      <button
        type="button"
        aria-label={t("profile.close")}
        onClick={handleBackdropClick}
        className="yano-profile-backdrop absolute inset-0 cursor-default"
      />

      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="profile-sheet-title"
        className="yano-profile-sheet relative flex max-h-[min(94vh,720px)] w-full max-w-[420px] flex-col"
      >
        <div className="yano-profile-sheet__handle" aria-hidden="true" />

        <form onSubmit={handleSubmit} className="flex min-h-0 flex-1 flex-col">
          <header className="flex items-start justify-between gap-3 px-6 pb-2 pt-2 sm:pt-5">
            <div className="min-w-0 flex-1">
              <h2 id="profile-sheet-title" className="font-display text-[clamp(1.65rem,6vw,2rem)] font-extrabold leading-[0.98] tracking-[-0.035em] text-white">
                {hasExistingSession ? t("profile.title_edit") : t("profile.title_new")}
              </h2>
            </div>

            <button
              type="button"
              onClick={onClose}
              disabled={busy}
              aria-label={t("profile.close")}
              className="yano-profile-sheet__close shrink-0"
            >
              <svg viewBox="0 0 20 20" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
                <path d="M5 5l10 10M15 5L5 15" />
              </svg>
            </button>
          </header>

          {avatarDraft?.editing ? (
            <>
              <div className="min-h-0 flex-1 overflow-y-auto px-6 pb-4 pt-6">
                <div className="yano-profile-photo yano-profile-photo--editing">
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept={AVATAR_ACCEPT}
                    className="sr-only"
                    onChange={handleAvatarFileChange}
                  />

                  <div className="yano-profile-photo__stage" aria-hidden="true">
                    {renderPhotoStage()}
                  </div>

                  <div className="yano-profile-photo__editor">
                    <label className="yano-profile-photo__range">
                      <span>{t("profile.photo_zoom")}</span>
                      <input
                        type="range"
                        min={AVATAR_ZOOM_MIN}
                        max={AVATAR_ZOOM_MAX}
                        step="0.01"
                        value={avatarZoomValue(avatarDraft)}
                        onChange={(event) => updateAvatarZoom(event.target.value)}
                        disabled={busy}
                      />
                    </label>
                  </div>

                  {avatarError ? (
                    <div className="mt-3 rounded-2xl border border-red-400/30 bg-red-500/10 px-4 py-3 text-[13px] font-semibold text-red-100">
                      {avatarError}
                    </div>
                  ) : null}
                </div>
              </div>

              <footer className="flex gap-3 px-6 pb-[max(1.1rem,env(safe-area-inset-bottom))] pt-3">
                <button
                  type="button"
                  onClick={clearAvatarDraft}
                  disabled={busy}
                  className="yano-button yano-button--secondary flex-1"
                >
                  {t("profile.photo_cancel")}
                </button>
                <button
                  type="button"
                  onClick={handleSaveAvatarDraft}
                  disabled={busy}
                  className="yano-button yano-button--primary flex-1"
                >
                  {avatarSaving ? t("profile.photo_uploading") : t("profile.photo_save")}
                </button>
              </footer>
            </>
          ) : (
            <>
              <div className="min-h-0 flex-1 overflow-y-auto px-6 pb-2 pt-3">
                <div className="flex justify-center">
                  <div className="yano-profile-photo">
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept={AVATAR_ACCEPT}
                      className="sr-only"
                      onChange={handleAvatarFileChange}
                    />

                    <button
                      type="button"
                      onClick={openFilePicker}
                      disabled={!canManageAvatar}
                      aria-label={t("profile.add_photo")}
                      title={t("profile.add_photo")}
                      className="yano-profile-photo__button group"
                    >
                      <span className="yano-profile-photo__stage" aria-hidden="true">
                        {renderPhotoStage()}
                      </span>
                    </button>

                    {avatarSaving || avatarRemoving ? (
                      <span className="yano-profile-photo__caption">
                        {avatarSaving ? t("profile.photo_uploading") : t("profile.photo_removing")}
                      </span>
                    ) : null}

                    {avatarError ? (
                      <div className="mt-3 rounded-2xl border border-red-400/30 bg-red-500/10 px-4 py-3 text-[13px] font-semibold text-red-100">
                        {avatarError}
                      </div>
                    ) : null}

                    {avatarUrl || avatarDraft ? (
                      <div className="yano-profile-photo__actions">
                        <button
                          type="button"
                          onClick={avatarUrl ? handleRemoveAvatar : clearAvatarDraft}
                          disabled={!canManageAvatar}
                          className="yano-profile-photo__action"
                        >
                          {avatarRemoving ? t("profile.photo_removing") : t("profile.photo_remove")}
                        </button>
                      </div>
                    ) : null}
                  </div>
                </div>

                <div className="mt-7">
                  <label htmlFor="profile-name" className="yano-profile-label">
                    {t("profile.name_label")} <span className="text-[var(--melon)]">*</span>
                  </label>
                  <input
                    id="profile-name"
                    ref={nameInputRef}
                    type="text"
                    inputMode="text"
                    autoComplete="given-name"
                    maxLength={40}
                    value={draft.display_name}
                    onChange={(event) => updateField("display_name", event.target.value)}
                    placeholder={t("profile.name_placeholder")}
                    className="yano-profile-input mt-2"
                  />
                </div>

                <div className="mt-5">
                  <div className="grid grid-cols-3 gap-2" role="group" aria-label={t("profile.gender_label")}>
                    {genderOptions.map((option) => {
                      const active = draft.gender === option.value
                      return (
                        <button
                          key={option.value}
                          type="button"
                          onClick={() => updateField("gender", option.value)}
                          aria-pressed={active}
                          className={`yano-profile-chip yano-profile-chip--${option.value} ${active ? "yano-profile-chip--active" : ""}`}
                        >
                          <span className="yano-profile-chip__icon">
                            <ProfileGenderIcon value={option.value} />
                          </span>
                          <span className="yano-profile-chip__label">{option.label}</span>
                        </button>
                      )
                    })}
                  </div>
                </div>

                <div className="mt-5">
                  <div className="flex items-baseline justify-between gap-3">
                    <label htmlFor="profile-bio" className="yano-profile-label">
                      {t("profile.bio_label")}
                      <span className="ml-1.5 text-[10px] font-bold tracking-[0.16em] text-violet-300/40">
                        {t("profile.bio_optional")}
                      </span>
                    </label>
                    <span className={`text-[10px] font-bold tabular-nums ${bioLength >= 180 ? "text-[var(--melon)]" : "text-violet-300/40"}`}>
                      {bioLength}/200
                    </span>
                  </div>
                  <textarea
                    id="profile-bio"
                    value={draft.bio}
                    onChange={(event) => updateField("bio", event.target.value.slice(0, 200))}
                    placeholder={t("profile.bio_placeholder")}
                    rows={3}
                    className="yano-profile-input yano-profile-input--textarea mt-2"
                  />
                </div>

                {errorMessage ? (
                  <div className="mt-4 rounded-2xl border border-red-400/30 bg-red-500/10 px-4 py-3 text-[13px] font-semibold text-red-100">
                    {errorMessage}
                  </div>
                ) : null}
              </div>

              <footer className="px-6 pb-[max(1.1rem,env(safe-area-inset-bottom))] pt-3">
                <button
                  type="submit"
                  disabled={!canSave}
                  className="yano-button yano-button--primary"
                >
                  {saving ? t("profile.saving") : (hasExistingSession ? t("profile.cta_edit") : t("profile.cta_new"))}
                </button>
              </footer>
            </>
          )}
        </form>
      </div>
    </div>
  )
}

const EFFECTS_IMAGES = ["/effects-1.webp", "/effects-2.webp", "/effects-3.webp"]

function EffectsShowcase() {
  const [index, setIndex] = useState(0)

  useEffect(() => {
    const interval = window.setInterval(() => setIndex((i) => (i + 1) % EFFECTS_IMAGES.length), 800)
    return () => window.clearInterval(interval)
  }, [])

  return (
    <div className="relative aspect-[4/3] w-full max-w-xs overflow-hidden rounded-2xl border border-white/8 sm:w-64">
      {EFFECTS_IMAGES.map((src, i) => (
        <img
          key={src}
          src={src}
          alt=""
          loading="lazy"
          className="absolute inset-0 h-full w-full object-cover transition-opacity duration-150 ease-in-out"
          style={{ opacity: i === index ? 1 : 0 }}
        />
      ))}
    </div>
  )
}

function RoomMemberPill({ member }) {
  const { t } = useI18n()

  return (
    <span className="yano-member-pill">
      <span className="font-semibold">{member.display_name}</span>
      {member.role === "host" ? <span className="text-white/50">{t("room.host")}</span> : null}
    </span>
  )
}

function StatusBanner({ tone = "neutral", children }) {
  const className = [
    "rounded-[1rem] border px-4 py-3 text-sm font-semibold",
    tone === "error" ? "border-red-500/30 bg-red-500/14 text-red-100" : "",
    tone === "success" ? "border-violet-500/30 bg-violet-500/14 text-violet-100" : "",
    tone === "neutral" ? "border-zinc-700 bg-zinc-900 text-zinc-300" : ""
  ].filter(Boolean).join(" ")

  return <div className={className}>{children}</div>
}

function EndedScreen({ room, onRejoin, onReturnHome, submitting = false }) {
  const { t } = useI18n()

  return (
    <section className="grid min-h-0 flex-1 place-items-center p-4">
      <div className="w-full max-w-md text-center">
        <p className="font-display text-[clamp(2.4rem,9vw,3.6rem)] font-extrabold leading-[1] tracking-[-0.04em] text-violet-100">{t("ended.heading")}</p>
        <p className="mt-3 text-sm font-semibold text-violet-400/70 sm:text-base">
          {room?.title ? t("ended.room_ended", { title: room.title }) : t("ended.left")}
        </p>
        {room?.code ? (
          <p className="mt-3 text-xs font-extrabold uppercase tracking-[0.18em] text-violet-500/50">
            Room {room.code}
          </p>
        ) : null}
        <div className="mt-10 grid gap-3">
          <button className="yano-button yano-button--primary" type="button" disabled={submitting} onClick={onRejoin}>
            {submitting ? t("ended.rejoining") : t("ended.rejoin")}
          </button>
          <button className="yano-button yano-button--secondary" type="button" onClick={onReturnHome}>
            {t("ended.home")}
          </button>
        </div>
      </div>
    </section>
  )
}

export function App() {
  const { t, locale } = useI18n()
  const storedSession = loadStoredSession()
  const [session, setSession] = useState(storedSession)
  const [profile, setProfile] = useState(() => normalizeProfile(storedSession?.user || EMPTY_FORM))
  const [profileSheetOpen, setProfileSheetOpen] = useState(false)
  const [profileSheetSaving, setProfileSheetSaving] = useState(false)
  const [profileSheetError, setProfileSheetError] = useState("")
  const [inviteRoom, setInviteRoom] = useState(null)
  const [roomCode, setRoomCode] = useState(currentRoomCodeFromPath())
  const [copied, setCopied] = useState(false)
  const [notice, setNotice] = useState("")
  const [error, setError] = useState("")
  const [submitting, setSubmitting] = useState(false)
  const [roomLoading, setRoomLoading] = useState(false)
  const [meetingStarted, setMeetingStarted] = useState(false)
  const [endedRoom, setEndedRoom] = useState(null)
  const [mediaPreferences, setMediaPreferences] = useState({
    audioEnabled: true,
    videoEnabled: true
  })
  const [prejoinEffectsOpen, setPrejoinEffectsOpen] = useState(false)
  const [prejoinGrade, setPrejoinGrade] = useState(() => {
    try {
      const legacy = localStorage.getItem("yano_prejoin_effect")
      if (legacy && PREJOIN_GRADES.some((item) => item.id === legacy)) return legacy
      if (legacy === "portrait") return "pop"

      return localStorage.getItem("yano_prejoin_grade") || "pop"
    } catch (_error) {
      return "pop"
    }
  })
  const [prejoinSoftEnabled, setPrejoinSoftEnabled] = useState(() => {
    try {
      const legacy = localStorage.getItem("yano_prejoin_effect")
      if (legacy === "portrait") return true

      const stored = localStorage.getItem("yano_prejoin_soft")
      return stored ? stored === "1" : false
    } catch (_error) {
      return false
    }
  })
  const [prejoinVignetteEnabled, setPrejoinVignetteEnabled] = useState(() => {
    try {
      const stored = localStorage.getItem("yano_prejoin_vignette")
      return stored ? stored === "1" : true
    } catch (_error) {
      return true
    }
  })
  const [prejoinFaceBlurEnabled, setPrejoinFaceBlurEnabled] = useState(() => {
    try {
      const stored = localStorage.getItem("yano_prejoin_face_blur")
      return stored ? stored === "1" : false
    } catch (_error) {
      return false
    }
  })
  const [prejoinFaceBlurAmount, setPrejoinFaceBlurAmount] = useState(() => {
    try {
      const raw = localStorage.getItem("yano_prejoin_face_blur_amount")
      const value = raw ? Number(raw) : 6
      if (!Number.isFinite(value)) return 6
      return Math.min(9, Math.max(2, Math.round(value)))
    } catch (_error) {
      return 6
    }
  })
  const [chatMessages, setChatMessages] = useState([])
  const [chatOpen, setChatOpen] = useState(false)
  const [unreadCount, setUnreadCount] = useState(0)
  const [chatHasMore, setChatHasMore] = useState(false)
  const [chatLoading, setChatLoading] = useState(false)
  const [chatConnected, setChatConnected] = useState(false)
  const [chatOverlayTick, setChatOverlayTick] = useState(0)
  const [joinByCodeOpen, setJoinByCodeOpen] = useState(false)
  const [joinCodeDraft, setJoinCodeDraft] = useState("")
  const chatConnectionRef = useRef(null)
  const chatOpenRef = useRef(false)
  const currentUserIdRef = useRef(storedSession?.user?.id || null)
  const pendingProfileActionRef = useRef(null)
  const inviteJoined = inviteRoom?.membership?.status === "joined"
  const invitePending = inviteRoom?.membership?.status === "pending"

  useEffect(() => {
    bootstrap()
  }, [])

  useEffect(() => {
    if (!roomCode) {
      setInviteRoom(null)
      setMeetingStarted(false)
      return
    }

    loadRoom(roomCode)
  }, [roomCode, session?.api_token])

  useEffect(() => {
    setCopied(false)
  }, [inviteRoom?.code])

  useEffect(() => {
    chatOpenRef.current = chatOpen

    if (chatOpen) {
      setUnreadCount(0)
    }
  }, [chatOpen])

  useEffect(() => {
    currentUserIdRef.current = session?.user?.id || null
  }, [session?.user?.id])

  useEffect(() => {
    if (roomCode) {
      setEndedRoom(null)
    }
  }, [roomCode])

  useEffect(() => {
    let cancelled = false

    function teardownChat(clearState = true) {
      if (chatConnectionRef.current) {
        chatConnectionRef.current.unsubscribe()
        chatConnectionRef.current = null
      }

      setChatConnected(false)

      if (clearState) {
        setChatMessages([])
        setChatHasMore(false)
        setUnreadCount(0)
        setChatOpen(false)
        setChatLoading(false)
      }
    }

    async function bootstrapChat() {
      if (!session?.api_token || !inviteRoom?.code || (!inviteJoined && !invitePending)) {
        teardownChat()
        return
      }

      teardownChat(false)
      setChatMessages([])
      setChatHasMore(false)
      setUnreadCount(0)

      try {
        const connection = subscribeToRoomChannel(session.api_token, inviteRoom.code, {
          onConnected() {
            if (!cancelled) {
              setChatConnected(true)
            }
          },

          onDisconnected() {
            if (!cancelled) {
              setChatConnected(false)
            }
          },

          onRejected() {
            setChatConnected(false)
            setError(t("app.chat_connection_rejected"))
          },

          onEvent(data) {
            if (data?.type === "new_message" && data.message) {
              setChatMessages((current) => mergeMessages(current, [ { ...data.message, _receivedAt: Date.now() } ]))

              if (!chatOpenRef.current && data.message.sender.id !== currentUserIdRef.current) {
                setUnreadCount((count) => count + 1)
              }

              return
            }

            if (data?.type === "member_joined" && data.member) {
              setInviteRoom((current) => {
                if (!current) return current
                const nextPresenceCount = Number.isFinite(Number(data.presence_count)) ? Number(data.presence_count) : current.presence_count
                const members = current.members || []
                const existing = members.find((m) => m.id === data.member.id)
                if (existing) {
                  return {
                    ...current,
                    presence_count: nextPresenceCount,
                    members: members.map((m) => m.id === data.member.id ? { ...m, ...data.member, present_in_call: true } : m)
                  }
                }
                return { ...current, presence_count: nextPresenceCount, members: [ ...members, { ...data.member, present_in_call: true } ] }
              })
              return
            }

            if (data?.type === "member_left" && data.user_id) {
              setInviteRoom((current) => {
                if (!current) return current
                const nextPresenceCount = Number.isFinite(Number(data.presence_count)) ? Number(data.presence_count) : current.presence_count
                return {
                  ...current,
                  presence_count: nextPresenceCount,
                  members: (current.members || []).map((m) => m.id === data.user_id ? { ...m, present_in_call: false } : m)
                }
              })
              return
            }

            if (data?.type === "chat_error" && data.error) {
              setError(data.error)
              return
            }

            if (data?.type === "room_locked") {
              setInviteRoom((current) => current ? { ...current, locked: true, locked_at: data.locked_at } : current)
              return
            }

            if (data?.type === "room_unlocked") {
              setInviteRoom((current) => current ? { ...current, locked: false, locked_at: null } : current)
              return
            }

            if (data?.type === "member_kicked") {
              if (data.user_id === currentUserIdRef.current) {
                handleLeaveRoom()
                return
              }

              setInviteRoom((current) => {
                if (!current) return current
                return { ...current, members: (current.members || []).filter((m) => m.id !== data.user_id) }
              })
              return
            }

            if (data?.type === "member_muted") {
              setInviteRoom((current) => {
                if (!current) return current
                return {
                  ...current,
                  members: (current.members || []).map((m) =>
                    m.id === data.user_id ? { ...m, muted_by_host: data.muted } : m
                  )
                }
              })
              return
            }

            if (data?.type === "member_requested") {
              setInviteRoom((current) => {
                if (!current) return current
                const pending = current.pending_members || []
                if (pending.some((m) => m.id === data.user_id)) return current
                return { ...current, pending_members: [...pending, { id: data.user_id, display_name: data.display_name }] }
              })
              return
            }

            if (data?.type === "join_approved") {
              if (data.user_id === currentUserIdRef.current) {
                refreshRoom()
                return
              }

              setInviteRoom((current) => {
                if (!current) return current
                const pending = (current.pending_members || []).filter((m) => m.id !== data.user_id)
                const alreadyMember = (current.members || []).some((m) => m.id === data.user_id)
                const members = alreadyMember ? current.members : [...(current.members || []), { id: data.user_id, display_name: data.display_name, role: "guest", muted_by_host: false }]
                return { ...current, pending_members: pending, members }
              })
              return
            }

            if (data?.type === "join_denied") {
              if (data.user_id === currentUserIdRef.current) {
                setInviteRoom(null)
                setRoomCode("")
                setRoomPath("")
                setError(t("home.join_denied"))
                return
              }

              setInviteRoom((current) => {
                if (!current) return current
                return { ...current, pending_members: (current.pending_members || []).filter((m) => m.id !== data.user_id) }
              })
              return
            }
          }
        })

        if (cancelled) {
          connection.unsubscribe()
          return
        }

        chatConnectionRef.current = connection

        if (inviteJoined) {
          setChatLoading(true)
          const payload = await api.getRoomMessages(session.api_token, inviteRoom.code)
          if (cancelled) return

          setChatMessages((current) => mergeMessages(current, payload.messages || [], true))
          setChatHasMore(Boolean(payload.has_more))
        }
      } catch (event) {
        if (!cancelled) {
          setChatConnected(false)
          setError(event.message)
        }
      } finally {
        if (!cancelled) {
          setChatLoading(false)
        }
      }
    }

    bootstrapChat()

    return () => {
      cancelled = true
      teardownChat()
    }
  }, [session?.api_token, inviteRoom?.code, inviteJoined, invitePending, t])

  const stageJoin = inviteRoom?.join
  const inMeeting = Boolean(stageJoin && meetingStarted)
  const previewName = session?.user?.display_name || profile.display_name || t("call.guest")
  const localVideoEffects = useMemo(
    () => normalizeVideoEffects({
      grade: prejoinGrade,
      soft: prejoinSoftEnabled,
      vignette: prejoinVignetteEnabled,
      faceBlur: prejoinFaceBlurEnabled,
      faceBlurPx: prejoinFaceBlurAmount
    }),
    [prejoinGrade, prejoinSoftEnabled, prejoinVignetteEnabled, prejoinFaceBlurEnabled, prejoinFaceBlurAmount]
  )
  const roomMembers = inviteRoom?.members || []
  const termsHref = locale === "en" ? "/terms" : `/terms?lang=${encodeURIComponent(locale)}`
  const marketingUseCases = [
    t("home.marketing.use_case.study_group"),
    t("home.marketing.use_case.game_night"),
    t("home.marketing.use_case.standup"),
    t("home.marketing.use_case.watch_party"),
    t("home.marketing.use_case.catch_up"),
    t("home.marketing.use_case.jam_session"),
    t("home.marketing.use_case.book_club"),
    t("home.marketing.use_case.coworking")
  ]

  const stageTitle = inviteRoom?.title || t("app.room_default_title")

  const prejoinHeading = useMemo(() => {
    if (roomLoading) return t("home.heading.loading")
    if (!inviteRoom) return t("home.heading.default")
    if (invitePending) return t("home.heading.pending")
    if (!inviteJoined) return t("home.heading.join")
    return t("home.heading.joined")
  }, [inviteJoined, invitePending, inviteRoom, roomLoading, t])

  const prejoinBody = useMemo(() => {
    if (roomLoading) return t("home.body.loading")
    if (!inviteRoom) return t("home.body.default")
    if (invitePending) return t("home.body.pending")
    if (!inviteJoined) return t("home.body.join")
    const countRaw = inviteRoom?.presence_count
    const count = Number.isFinite(Number(countRaw)) ? Number(countRaw) : roomMembers.length
    const people = count === 1 ? t("home.people_one") : t("home.people_other")
    return `${count} ${people} ${t("home.body.joined_in_room")}`
  }, [inviteJoined, invitePending, inviteRoom, roomLoading, roomMembers.length, t])

  const primaryActionLabel = useMemo(() => {
    if (roomLoading) return t("home.cta.opening")
    if (!inviteRoom) return t("home.cta.create")
    if (invitePending) return t("home.cta.pending")
    if (!inviteJoined) return t("home.cta.join")
    return t("home.cta.enter")
  }, [inviteJoined, invitePending, inviteRoom, roomLoading, t])

  async function bootstrap() {
    if (!storedSession?.api_token) return

    try {
      const me = await api.getMe(storedSession.api_token)
      persistSessionUser(me.user, storedSession.api_token)
    } catch (event) {
      saveStoredSession(null)
      setSession(null)
      setInviteRoom(null)
      setProfile(EMPTY_FORM)
      setError(event.message)
    }
  }

  function persistSessionUser(user, apiToken = session?.api_token || user?.api_token) {
    const nextSession = {
      api_token: apiToken,
      user
    }

    saveStoredSession(nextSession)
    setSession(nextSession)
    setProfile(normalizeProfile(user))
    setInviteRoom((current) => mergeUserIntoRoom(current, user))
    setChatMessages((current) => mergeUserIntoMessages(current, user))

    return nextSession
  }

  async function ensureSession() {
    if (!session?.api_token) {
      setProfileSheetError("")
      setProfileSheetOpen(true)
      throw new Error(t("home.error.profile_required"))
    }
    return session
  }

  function openProfileSheet() {
    setProfileSheetError("")
    setProfileSheetOpen(true)
  }

  function closeProfileSheet() {
    if (profileSheetSaving) return
    pendingProfileActionRef.current = null
    setProfileSheetOpen(false)
    setProfileSheetError("")
  }

  function queueProfileAction(action) {
    pendingProfileActionRef.current = action
    openProfileSheet()
  }

  async function createRoomWithSession(activeSession) {
    const payload = await api.createRoom(activeSession.api_token, {})
    setInviteRoom(payload.room)
    setRoomCode(payload.room.code)
    setRoomPath(payload.room.code)
    setNotice("")
    setMeetingStarted(true)
    setEndedRoom(null)
  }

  async function joinRoomByCodeWithSession(activeSession, code) {
    const payload = await api.joinRoom(activeSession.api_token, code)
    setInviteRoom(payload.room)
    setRoomCode(payload.room.code)
    setMeetingStarted(true)
    setEndedRoom(null)
    setChatOpen(false)
  }

  async function resumePendingProfileAction(activeSession) {
    const pendingAction = pendingProfileActionRef.current
    pendingProfileActionRef.current = null
    if (!pendingAction) return

    setSubmitting(true)
    setError("")
    setNotice("")

    try {
      if (pendingAction.type === "create_room") {
        await createRoomWithSession(activeSession)
      } else if (pendingAction.type === "join_room" && pendingAction.code) {
        await joinRoomByCodeWithSession(activeSession, pendingAction.code)
      }
    } catch (event) {
      setError(event.message)
    } finally {
      setSubmitting(false)
    }
  }

  async function handleSaveProfile(payload, stagedAvatar = null) {
    setProfileSheetSaving(true)
    setProfileSheetError("")

    try {
      const result = session?.api_token
        ? await api.updateMe(session.api_token, payload)
        : await api.createSession(payload)

      const token = session?.api_token || result.user.api_token
      let user = result.user

      if (stagedAvatar) {
        try {
          const avatarResult = await api.uploadAvatar(
            token,
            stagedAvatar.file,
            avatarCropPayload(stagedAvatar),
            { width: stagedAvatar.width, height: stagedAvatar.height }
          )
          user = avatarResult.user
        } catch (avatarError) {
          console.error("avatar upload failed after signup", avatarError)
        }
      }

      const persisted = persistSessionUser(user, token)

      setProfileSheetOpen(false)
      await resumePendingProfileAction(persisted)
    } catch (event) {
      setProfileSheetError(event.message || t("profile.error.save_failed"))
    } finally {
      setProfileSheetSaving(false)
    }
  }

  async function handleProfileAvatarUpload(file, crop, source) {
    const result = await api.uploadAvatar(session.api_token, file, crop, source)
    persistSessionUser(result.user, session.api_token)
    return result.user
  }

  async function handleProfileAvatarCropUpdate(crop) {
    const result = await api.updateAvatarCrop(session.api_token, crop)
    persistSessionUser(result.user, session.api_token)
    return result.user
  }

  async function handleProfileAvatarRemove() {
    const result = await api.deleteAvatar(session.api_token)
    persistSessionUser(result.user, session.api_token)
    return result.user
  }

  async function loadRoom(code) {
    setRoomLoading(true)
    setError("")

    try {
      const payload = await api.getRoom(code, session?.api_token)
      setInviteRoom(payload.room)
    } catch (event) {
      setInviteRoom(null)
      if (event?.code === "room_not_found") {
        setError(t("home.error.room_not_found"))
      } else {
        setError(event.message)
      }
    } finally {
      setRoomLoading(false)
    }
  }

  async function handleCreateRoom() {
    setSubmitting(true)
    setError("")
    setNotice("")

    try {
      const activeSession = await ensureSession()
      await createRoomWithSession(activeSession)
    } catch (event) {
      setError(event.message)
    } finally {
      setSubmitting(false)
    }
  }

  async function handleJoinRoom() {
    await handleJoinRoomFromCode(roomCode)
  }

  function handleEnterRoom() {
    setMeetingStarted(true)
    setNotice("")
    setError("")
  }

  async function handleLeaveRoom() {
    if (!session?.api_token || !inviteRoom?.code) return

    setSubmitting(true)
    setError("")

    try {
      const endedSnapshot = inviteRoom ? {
        code: inviteRoom.code,
        title: inviteRoom.title,
        share_url: inviteRoom.share_url
      } : null
      await api.leaveRoom(session.api_token, inviteRoom.code)
      setEndedRoom(endedSnapshot)
      setInviteRoom(null)
      setRoomCode("")
      setCopied(false)
      setMeetingStarted(false)
      setChatOpen(false)
      setRoomPath("")
      setNotice("")
    } catch (event) {
      setError(event.message)
    } finally {
      setSubmitting(false)
    }
  }

  async function handleLockRoom() {
    if (!session?.api_token || !inviteRoom?.code) return
    setInviteRoom((current) => current ? { ...current, locked: true } : current)
    try {
      await api.lockRoom(session.api_token, inviteRoom.code)
    } catch (event) {
      setInviteRoom((current) => current ? { ...current, locked: false } : current)
      setError(event.message)
    }
  }

  async function handleUnlockRoom() {
    if (!session?.api_token || !inviteRoom?.code) return
    setInviteRoom((current) => current ? { ...current, locked: false } : current)
    try {
      await api.unlockRoom(session.api_token, inviteRoom.code)
    } catch (event) {
      setInviteRoom((current) => current ? { ...current, locked: true } : current)
      setError(event.message)
    }
  }

  async function handleKickMember(userId) {
    if (!session?.api_token || !inviteRoom?.code) return
    try {
      await api.kickMember(session.api_token, inviteRoom.code, userId)
    } catch (event) {
      setError(event.message)
    }
  }

  async function handleMuteMember(userId, muted = true) {
    if (!session?.api_token || !inviteRoom?.code) return
    try {
      await api.muteMember(session.api_token, inviteRoom.code, userId, muted)
    } catch (event) {
      setError(event.message)
    }
  }

  async function refreshRoom() {
    if (!session?.api_token || !inviteRoom?.code) return
    try {
      const payload = await api.getRoom(inviteRoom.code, session.api_token)
      if (payload?.room) setInviteRoom(payload.room)
    } catch (_event) {
      // silently fail
    }
  }

  async function handleApproveMember(userId) {
    if (!session?.api_token || !inviteRoom?.code) return
    try {
      await api.approveMember(session.api_token, inviteRoom.code, userId)
    } catch (event) {
      setError(event.message)
    }
  }

  async function handleDenyMember(userId) {
    if (!session?.api_token || !inviteRoom?.code) return
    try {
      await api.denyMember(session.api_token, inviteRoom.code, userId)
    } catch (event) {
      setError(event.message)
    }
  }

  async function copyRoomLink() {
    if (!inviteRoom?.share_url) return

    try {
      let urlToCopy = inviteRoom.share_url
      try {
        const next = new URL(inviteRoom.share_url, window.location.origin)
        next.searchParams.set("lang", locale)
        urlToCopy = next.toString()
      } catch (_error) {
        const sep = inviteRoom.share_url.includes("?") ? "&" : "?"
        urlToCopy = `${inviteRoom.share_url}${sep}lang=${encodeURIComponent(locale)}`
      }

      await navigator.clipboard.writeText(urlToCopy)
      setCopied(true)
      window.setTimeout(() => setCopied(false), 1500)
    } catch (_error) {
      setError(t("app.copy_failed"))
    }
  }

  function handleSetPrejoinGrade(gradeId) {
    setPrejoinGrade(gradeId)
    try {
      localStorage.setItem("yano_prejoin_grade", gradeId)
    } catch (_error) {}
  }

  function handleTogglePrejoinSoft() {
    setPrejoinSoftEnabled((value) => {
      const next = !value
      try {
        localStorage.setItem("yano_prejoin_soft", next ? "1" : "0")
      } catch (_error) {}
      return next
    })
  }

  function handleTogglePrejoinVignette() {
    setPrejoinVignetteEnabled((value) => {
      const next = !value
      try {
        localStorage.setItem("yano_prejoin_vignette", next ? "1" : "0")
      } catch (_error) {}
      return next
    })
  }

  function handleTogglePrejoinFaceBlur() {
    setPrejoinFaceBlurEnabled((value) => {
      const next = !value
      try {
        localStorage.setItem("yano_prejoin_face_blur", next ? "1" : "0")
      } catch (_error) {}
      return next
    })
  }

  function handleSetPrejoinFaceBlurAmount(nextValue) {
    const value = Math.min(9, Math.max(2, Math.round(Number(nextValue) || 2)))
    setPrejoinFaceBlurAmount(value)
    try {
      localStorage.setItem("yano_prejoin_face_blur_amount", String(value))
    } catch (_error) {}
  }

  function renderPrimaryAction() {
    const hasProfile = Boolean(session?.user?.display_name)

    if (roomLoading) {
      return (
        <button className="yano-button yano-button--primary" type="button" disabled>
          {t("home.cta.opening")}
        </button>
      )
    }

    if (!inviteRoom) {
      return (
        <>
          <button
            className="yano-button yano-button--primary"
            type="button"
            disabled={submitting}
            onClick={hasProfile ? handleCreateRoom : () => queueProfileAction({ type: "create_room" })}
          >
            {submitting ? t("home.cta.creating") : (hasProfile ? primaryActionLabel : t("home.cta.set_up_profile"))}
          </button>
          {joinByCodeOpen ? (
            <div className="mt-3 flex items-center gap-2">
              <input
                type="text"
                value={joinCodeDraft}
                onChange={(event) => setJoinCodeDraft(event.target.value.toUpperCase())}
                onKeyDown={(event) => {
                  if (event.key === "Enter" && joinCodeDraft.trim()) {
                    if (hasProfile) handleJoinRoomFromCode(joinCodeDraft.trim())
                    else queueProfileAction({ type: "join_room", code: joinCodeDraft.trim() })
                  }
                }}
                placeholder={t("home.code_placeholder")}
                maxLength={6}
                className="min-w-0 flex-1 rounded-2xl border border-violet-500/15 bg-violet-500/8 px-4 py-2.5 text-center text-sm font-bold uppercase tracking-widest text-violet-100 outline-none transition placeholder:text-violet-500/40 placeholder:tracking-normal placeholder:font-semibold focus:border-violet-400/40"
                autoFocus
              />
              <button
                className="yano-button yano-button--secondary shrink-0"
                type="button"
                disabled={!joinCodeDraft.trim() || submitting}
                onClick={() => {
                  if (hasProfile) handleJoinRoomFromCode(joinCodeDraft.trim())
                  else queueProfileAction({ type: "join_room", code: joinCodeDraft.trim() })
                }}
              >
                {t("home.cta.join")}
              </button>
            </div>
          ) : (
            <button className="mt-3 text-sm font-semibold text-violet-300/70 transition hover:text-violet-200" type="button" onClick={() => setJoinByCodeOpen(true)}>
              {t("home.join_by_code")}
            </button>
          )}
        </>
      )
    }

    if (invitePending) {
      return (
        <button className="yano-button yano-button--primary" type="button" disabled>
          {primaryActionLabel}
        </button>
      )
    }

    if (!inviteJoined) {
      return (
        <button
          className="yano-button yano-button--primary"
          type="button"
          disabled={submitting}
          onClick={hasProfile ? handleJoinRoom : () => queueProfileAction({ type: "join_room", code: roomCode })}
        >
          {submitting ? t("home.cta.joining") : (hasProfile ? primaryActionLabel : t("home.cta.set_up_profile"))}
        </button>
      )
    }

    return (
      <button className="yano-button yano-button--primary" type="button" onClick={handleEnterRoom}>
        {primaryActionLabel}
      </button>
    )
  }

  async function handleRejoinEndedRoom() {
    if (!endedRoom?.code) return

    setRoomCode(endedRoom.code)
    setRoomPath(endedRoom.code)
    setNotice("")
    setError("")
    await handleJoinRoomFromCode(endedRoom.code)
  }

  async function handleJoinRoomFromCode(code) {
    if (!code) return

    setSubmitting(true)
    setError("")
    setNotice("")

    try {
      const activeSession = await ensureSession()
      await joinRoomByCodeWithSession(activeSession, code)
    } catch (event) {
      setError(event.message)
    } finally {
      setSubmitting(false)
    }
  }

  function handleReturnHome() {
    if (chatConnectionRef.current) {
      chatConnectionRef.current.unsubscribe()
      chatConnectionRef.current = null
    }

    setEndedRoom(null)
    setInviteRoom(null)
    setRoomCode("")
    setCopied(false)
    setMeetingStarted(false)
    setNotice("")
    setError("")
    setChatMessages([])
    setChatHasMore(false)
    setUnreadCount(0)
    setChatOpen(false)
    setRoomPath("")
  }

  async function handleSendMessage(body) {
    if (!chatConnectionRef.current || !chatConnected) {
      throw new Error(t("app.chat_not_connected"))
    }

    chatConnectionRef.current.send("send_message", { body })
  }

  async function handleLoadMoreMessages() {
    if (!session?.api_token || !inviteRoom?.code || !chatHasMore || !chatMessages.length) return

    setChatLoading(true)

    try {
      const payload = await api.getRoomMessages(session.api_token, inviteRoom.code, chatMessages[0].id)
      setChatMessages((current) => mergeMessages(current, payload.messages || [], true))
      setChatHasMore(Boolean(payload.has_more))
    } catch (event) {
      setError(event.message)
    } finally {
      setChatLoading(false)
    }
  }

  function handleToggleChat() {
    setChatOpen((open) => !open)
  }

  useEffect(() => {
    if (!inMeeting) return
    const interval = window.setInterval(() => setChatOverlayTick((n) => n + 1), 5000)
    return () => window.clearInterval(interval)
  }, [inMeeting])

  if (inMeeting) {
    const now = Date.now()
    const chatOverlayMessages = chatMessages
      .filter((m) => m._receivedAt && now - m._receivedAt < 30000)
      .slice(-4)

    return (
      <main className="h-dvh overflow-hidden bg-[#1a0e2e] p-0">
          <CallStage
            join={stageJoin}
            title={stageTitle}
            roomCode={inviteRoom?.code}
            shareUrl={inviteRoom?.share_url}
            copied={copied}
            onCopyLink={copyRoomLink}
            onLeaveRoom={handleLeaveRoom}
            chatOpen={chatOpen}
            onToggleChat={handleToggleChat}
            unreadCount={unreadCount}
            onSendMessage={handleSendMessage}
            onLoadMoreMessages={handleLoadMoreMessages}
            chatOverlayMessages={chatOverlayMessages}
            chatMessages={chatMessages}
            chatHasMore={chatHasMore}
            chatLoading={chatLoading}
            chatConnected={chatConnected}
            apiToken={session?.api_token}
            currentUserId={session?.user?.id}
            displayName={previewName}
            initialMedia={mediaPreferences}
            localVideoEffects={localVideoEffects}
            isHost={inviteRoom?.membership?.role === "host"}
            roomLocked={Boolean(inviteRoom?.locked)}
            roomMembers={roomMembers}
            onLockRoom={handleLockRoom}
            onUnlockRoom={handleUnlockRoom}
            onKickMember={handleKickMember}
            onMuteMember={handleMuteMember}
            pendingMembers={inviteRoom?.pending_members || []}
            onApproveMember={handleApproveMember}
            onDenyMember={handleDenyMember}
          />
      </main>
    )
  }

  if (endedRoom) {
    return (
      <main className="yano-grain h-dvh overflow-hidden bg-[#1a0e2e] text-white">
        <div className="relative z-[1] mx-auto flex h-dvh max-w-md flex-col px-5 py-5 sm:px-8">
          <header className="flex items-center gap-3">
            <YanoLogo className="h-10 w-10" />
            <YanoWordmark />
          </header>

          <EndedScreen room={endedRoom} onRejoin={handleRejoinEndedRoom} onReturnHome={handleReturnHome} submitting={submitting} />
        </div>
      </main>
    )
  }

  return (
    <main className="yano-grain min-h-dvh bg-[#1a0e2e] text-white">
      <div className="relative z-[1] mx-auto flex min-h-dvh max-w-md flex-col px-5 py-5 sm:px-8 lg:max-w-5xl lg:px-10 lg:py-8">
        <header className="yano-enter relative z-[140] flex items-center justify-between overflow-visible" style={{ animationDelay: "0ms" }}>
          <div className="flex items-center gap-3">
            <YanoLogo className="h-10 w-10" />
            <YanoWordmark />
          </div>
          <div className="flex items-center gap-2">
            {inviteRoom?.code ? (
              <div className="rounded-full bg-violet-500/15 px-3 py-1.5 text-xs font-bold text-violet-300">
                {inviteRoom.code}
              </div>
            ) : null}
            <LocalePicker />
          </div>
        </header>

        <div className="relative z-0 flex flex-1 flex-col items-center justify-center py-8 lg:py-10">
          <div className="w-full lg:grid lg:grid-cols-[minmax(0,1.35fr)_minmax(0,0.95fr)] lg:items-start lg:gap-10">
            <div className="w-full yano-enter lg:col-start-2 lg:row-start-1" style={{ animationDelay: "60ms" }}>
              <h1 className="font-display text-[clamp(2.6rem,10vw,4rem)] font-extrabold leading-[0.95] tracking-[-0.045em] text-white">{prejoinHeading}</h1>
              <p className="mt-3 text-[0.95rem] leading-relaxed text-violet-300/60">{prejoinBody}</p>
            </div>

	            <div className="mt-8 w-full yano-enter lg:col-start-1 lg:row-start-1 lg:row-span-3 lg:mt-0" style={{ animationDelay: "120ms" }}>
			              <div
			                className="yano-prejoin-preview overflow-hidden rounded-[1.6rem] bg-violet-500/8 lg:rounded-[2rem]"
			                style={{
			                  "--yano-preview-filter": filterStringForVideoEffects(localVideoEffects),
			                  "--yano-face-blur": `${prejoinFaceBlurAmount}px`
			                }}
			              >
		                <div className="rounded-[1.6rem] bg-violet-500/20 p-[2px] lg:rounded-[2rem]">
			                  <CameraPreview
			                    className="aspect-[3/4] w-full sm:aspect-video"
			                    minimal
			                    vignette={prejoinVignetteEnabled}
			                    faceBlur={prejoinFaceBlurEnabled}
				                    label={previewName}
				                    videoEnabled={mediaPreferences.videoEnabled}
				                    audioEnabled={mediaPreferences.audioEnabled}
		                  />
		                </div>

	                <div
	                  className="grid transition-[grid-template-rows] duration-200 ease-[cubic-bezier(0.16,1,0.3,1)]"
	                  style={{ gridTemplateRows: prejoinEffectsOpen ? "1fr" : "0fr" }}
	                >
	                  <div className="overflow-hidden">
	                    <div className={`px-4 pt-3 ${prejoinEffectsOpen ? "opacity-100 translate-y-0" : "opacity-0 -translate-y-1"} transition-[opacity,transform] duration-200 ease-[cubic-bezier(0.16,1,0.3,1)]`}>
	                      <div className="flex flex-wrap gap-2">
	                        {PREJOIN_GRADES.map((effect) => {
	                          const selected = prejoinGrade === effect.id
	                          return (
	                            <button
	                              key={effect.id}
	                              type="button"
	                              onClick={() => handleSetPrejoinGrade(effect.id)}
	                              className={`rounded-2xl border px-3 py-2 text-xs font-extrabold tracking-[0.12em] transition ${
	                                selected
	                                  ? "border-violet-400/30 bg-violet-500/18 text-violet-100"
	                                  : "border-violet-500/12 bg-black/10 text-violet-300/70 hover:bg-violet-500/10 hover:text-violet-200"
	                              }`}
	                              title={t(`effects.grade.${effect.id}`)}
	                              aria-pressed={selected}
	                            >
	                              {t(`effects.grade.${effect.id}`)}
	                            </button>
	                          )
	                        })}
	                      </div>
		                      <div className="mt-3">
		                        <div className="h-px w-full bg-violet-500/10" />
		                        <div className="mt-2 flex flex-wrap items-center gap-2">
		                        <button
		                          type="button"
		                          onClick={handleTogglePrejoinSoft}
		                          className={`rounded-2xl border px-3 py-2 text-xs font-extrabold tracking-[0.12em] transition ${
		                            prejoinSoftEnabled
		                              ? "border-violet-400/30 bg-violet-500/18 text-violet-100"
	                              : "border-violet-500/12 bg-black/10 text-violet-300/70 hover:bg-violet-500/10 hover:text-violet-200"
	                          }`}
	                          aria-pressed={prejoinSoftEnabled}
	                          title={t("effects.soft")}
	                        >
		                          {t("effects.soft")}
		                        </button>
		                        <button
		                          type="button"
		                          onClick={handleTogglePrejoinVignette}
		                          className={`rounded-2xl border px-3 py-2 text-xs font-extrabold tracking-[0.12em] transition ${
	                            prejoinVignetteEnabled
	                              ? "border-violet-400/30 bg-violet-500/18 text-violet-100"
	                              : "border-violet-500/12 bg-black/10 text-violet-300/70 hover:bg-violet-500/10 hover:text-violet-200"
	                          }`}
	                          aria-pressed={prejoinVignetteEnabled}
	                          title={t("effects.vignette")}
	                        >
		                          {t("effects.vignette")}
		                        </button>
		                        <button
		                          type="button"
		                          onClick={handleTogglePrejoinFaceBlur}
		                          className={`rounded-2xl border px-3 py-2 text-xs font-extrabold tracking-[0.12em] transition ${
	                            prejoinFaceBlurEnabled
	                              ? "border-violet-400/30 bg-violet-500/18 text-violet-100"
	                              : "border-violet-500/12 bg-black/10 text-violet-300/70 hover:bg-violet-500/10 hover:text-violet-200"
	                          }`}
	                          aria-pressed={prejoinFaceBlurEnabled}
	                          title={t("effects.face_preview_only")}
	                        >
		                          {t("effects.face")}
		                        </button>
		                        </div>
		                      </div>

		                      {prejoinFaceBlurEnabled ? (
		                        <div className="mt-3 rounded-2xl border border-violet-500/12 bg-black/10 px-3 py-3">
		                          <div className="flex items-center justify-between gap-3">
		                            <div className="text-[11px] font-extrabold tracking-[0.14em] text-violet-200/90">{t("effects.face")}</div>
		                            <div className="text-[11px] font-bold text-violet-300/60">{prejoinFaceBlurAmount}px</div>
		                          </div>
		                          <input
		                            className="yano-range mt-2 w-full"
		                            type="range"
		                            min="2"
		                            max="9"
		                            step="1"
		                            value={prejoinFaceBlurAmount}
		                            onChange={(event) => handleSetPrejoinFaceBlurAmount(event.target.value)}
		                            aria-label={t("effects.face_strength")}
		                          />
		                        </div>
		                      ) : null}
		                    </div>
		                  </div>
		                </div>
	                <div className="relative flex items-center justify-between gap-3 px-4 py-3">
                  <button
                    type="button"
                    onClick={openProfileSheet}
                    className="yano-profile-trigger group min-w-0 flex-1"
                    aria-label={session?.user?.display_name ? t("home.profile_trigger_edit_aria") : t("home.profile_trigger_new_aria")}
                  >
                    {session?.user?.display_name ? (
                      <>
                        <ProfileAvatar name={session.user.display_name} avatarUrl={session.user.avatar_url} size={32} />
                        <span className="min-w-0 flex-1 truncate text-left text-sm font-bold text-violet-100">
                          {session.user.display_name}
                        </span>
                        <span className="yano-profile-trigger__edit shrink-0">
                          <svg viewBox="0 0 16 16" className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M11.5 2.5a1.6 1.6 0 0 1 2.3 2.3l-7.4 7.4-3 .7.7-3 7.4-7.4Z" />
                          </svg>
                        </span>
                      </>
                    ) : (
                      <>
                        <span className="yano-profile-trigger__avatar-empty shrink-0" aria-hidden="true">
                          <svg viewBox="0 0 18 18" className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
                            <path d="M9 3.5v11M3.5 9h11" />
                          </svg>
                        </span>
                        <span className="min-w-0 flex-1 truncate text-left text-sm font-bold text-violet-200/85">
                          {t("home.profile_trigger_new")}
                        </span>
                        <span className="yano-profile-trigger__arrow shrink-0" aria-hidden="true">→</span>
                      </>
                    )}
                  </button>
                  <div className="flex items-center gap-2">
                    <MediaToggle active={mediaPreferences.audioEnabled} label={mediaPreferences.audioEnabled ? t("mic.on") : t("mic.off")} onClick={() => setMediaPreferences((current) => ({ ...current, audioEnabled: !current.audioEnabled }))}>
                      <MicIcon />
                    </MediaToggle>
	                    <MediaToggle active={mediaPreferences.videoEnabled} label={mediaPreferences.videoEnabled ? t("video.on") : t("video.off")} onClick={() => setMediaPreferences((current) => ({ ...current, videoEnabled: !current.videoEnabled }))}>
	                      <VideoIcon />
	                    </MediaToggle>
	                    <button
	                      type="button"
	                      className="yano-media-toggle"
	                      onClick={() => setPrejoinEffectsOpen((value) => !value)}
	                      aria-pressed={prejoinEffectsOpen}
	                      aria-label={t("effects.title")}
	                      title={t("effects.title")}
	                    >
	                      <EffectsIcon />
	                    </button>
	                  </div>
	                </div>
              </div>
            </div>

            <div className="mt-8 w-full lg:col-start-2 lg:row-start-2 lg:mt-6">
              {error ? <div className="w-full yano-enter"><StatusBanner tone="error">{error}</StatusBanner></div> : null}
              {!error && notice ? <div className="w-full yano-enter"><StatusBanner tone="success">{notice}</StatusBanner></div> : null}

              <div className="mt-6 w-full yano-enter" style={{ animationDelay: "180ms" }}>
                {renderPrimaryAction()}
                {inviteRoom ? (
                  <div className="mt-3 flex items-center justify-center">
                    <div className="inline-flex items-center gap-1.5 rounded-full border border-violet-500/15 bg-violet-500/8 px-3 py-1.5 text-sm font-semibold text-violet-200/80">
                      <UsersIcon className="h-4 w-4 text-violet-300/70" />
                      <span className="tabular-nums">{inviteRoom?.presence_count ?? roomMembers.length}</span>
                    </div>
                  </div>
                ) : null}
              </div>
            </div>
          </div>
        </div>

      </div>

      <>
          <section className="relative z-[1] overflow-hidden border-t border-violet-500/8 bg-[#130a22]">
            <div className="pointer-events-none absolute inset-0" aria-hidden>
              {/* Connected nodes — top right */}
              <svg className="absolute right-[4%] top-[10%] opacity-[0.06] sm:opacity-[0.09]" style={{ width: "clamp(100px, 14vw, 200px)" }} viewBox="0 0 120 100" fill="none">
                <circle cx="20" cy="20" r="8" stroke="#a78bfa" strokeWidth="1.3" />
                <circle cx="100" cy="15" r="6" stroke="#a78bfa" strokeWidth="1.3" />
                <circle cx="60" cy="70" r="10" stroke="#a78bfa" strokeWidth="1.3" />
                <circle cx="105" cy="80" r="5" stroke="#a78bfa" strokeWidth="1.3" />
                <line x1="28" y1="24" x2="52" y2="62" stroke="#a78bfa" strokeWidth="1" opacity="0.5" />
                <line x1="68" y1="64" x2="94" y2="18" stroke="#a78bfa" strokeWidth="1" opacity="0.5" />
                <line x1="70" y1="72" x2="100" y2="78" stroke="#a78bfa" strokeWidth="1" opacity="0.5" />
              </svg>
              {/* Share arrow — bottom left */}
              <svg className="absolute bottom-[12%] left-[4%] opacity-[0.06] sm:opacity-[0.09]" style={{ width: "clamp(80px, 10vw, 150px)" }} viewBox="0 0 80 80" fill="none">
                <circle cx="15" cy="40" r="10" stroke="#c084fc" strokeWidth="1.3" />
                <circle cx="65" cy="15" r="10" stroke="#c084fc" strokeWidth="1.3" />
                <circle cx="65" cy="65" r="10" stroke="#c084fc" strokeWidth="1.3" />
                <line x1="25" y1="35" x2="55" y2="20" stroke="#c084fc" strokeWidth="1" opacity="0.5" />
                <line x1="25" y1="45" x2="55" y2="60" stroke="#c084fc" strokeWidth="1" opacity="0.5" />
              </svg>
            </div>
            <div className="relative mx-auto flex max-w-4xl flex-col gap-6 px-6 py-20 sm:flex-row sm:items-center sm:gap-14 sm:py-28">
                <div className="flex-1">
                  <h2 className="font-display text-[clamp(1.6rem,5vw,2.4rem)] font-extrabold leading-[1.05] tracking-[-0.04em] text-white">
                    {t("home.marketing.invite.title").split("\n").map((line, index) => (
                      <React.Fragment key={line}>
                        {index > 0 ? <br /> : null}
                        {line}
                      </React.Fragment>
                    ))}
                  </h2>
                  <p className="mt-3 text-[0.9rem] leading-relaxed text-violet-300/50">
                    {t("home.marketing.invite.body")}
                  </p>
                </div>
                <div className="flex shrink-0 items-center justify-center sm:w-60">
                  <div className="w-full max-w-[14rem]">
                    <svg viewBox="0 0 240 160" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full">
                      {/* Browser chrome */}
                      <rect x="1" y="1" width="238" height="158" rx="12" stroke="#a78bfa" strokeWidth="1" opacity="0.15" />
                      <rect x="1" y="1" width="238" height="24" rx="12" fill="#a78bfa" fillOpacity="0.06" />
                      <rect x="1" y="13" width="238" height="12" fill="#a78bfa" fillOpacity="0.06" />
                      {/* Window dots */}
                      <circle cx="16" cy="13" r="3" fill="#a78bfa" opacity="0.2" />
                      <circle cx="26" cy="13" r="3" fill="#a78bfa" opacity="0.15" />
                      <circle cx="36" cy="13" r="3" fill="#a78bfa" opacity="0.1" />
                      {/* URL bar */}
                      <rect x="52" y="8" width="136" height="10" rx="5" fill="#a78bfa" fillOpacity="0.08" />
                      <text x="72" y="16" fontSize="6" fontWeight="600" fill="#a78bfa" opacity="0.35" fontFamily="system-ui">{t("home.marketing.invite.preview_url")}</text>
                      {/* Video grid */}
                      <rect x="12" y="34" width="104" height="56" rx="8" fill="#a78bfa" fillOpacity="0.06" stroke="#a78bfa" strokeWidth="0.8" opacity="0.2" />
                      <rect x="124" y="34" width="104" height="56" rx="8" fill="#a78bfa" fillOpacity="0.06" stroke="#a78bfa" strokeWidth="0.8" opacity="0.2" />
                      <rect x="12" y="96" width="104" height="56" rx="8" fill="#a78bfa" fillOpacity="0.06" stroke="#a78bfa" strokeWidth="0.8" opacity="0.2" />
                      <rect x="124" y="96" width="104" height="56" rx="8" fill="#a78bfa" fillOpacity="0.06" stroke="#a78bfa" strokeWidth="0.8" opacity="0.2" />
                      {/* Participant avatars */}
                      <circle cx="64" cy="58" r="10" stroke="#c084fc" strokeWidth="1" opacity="0.3" />
                      <circle cx="176" cy="58" r="10" stroke="#c084fc" strokeWidth="1" opacity="0.3" />
                      <circle cx="64" cy="120" r="10" stroke="#c084fc" strokeWidth="1" opacity="0.3" />
                      <circle cx="176" cy="120" r="10" stroke="#c084fc" strokeWidth="1" opacity="0.2" strokeDasharray="3 3" />
                      {/* Plus icon in empty slot */}
                      <line x1="172" y1="120" x2="180" y2="120" stroke="#c084fc" strokeWidth="1" opacity="0.2" />
                      <line x1="176" y1="116" x2="176" y2="124" stroke="#c084fc" strokeWidth="1" opacity="0.2" />
                      {/* Names under avatars */}
                      <rect x="50" y="72" width="28" height="3" rx="1.5" fill="#a78bfa" opacity="0.15" />
                      <rect x="162" y="72" width="28" height="3" rx="1.5" fill="#a78bfa" opacity="0.15" />
                      <rect x="50" y="134" width="28" height="3" rx="1.5" fill="#a78bfa" opacity="0.15" />
                    </svg>
                  </div>
                </div>
              </div>
            </section>

            <section className="relative z-[1] overflow-hidden bg-[#0f0720]">
              <div className="pointer-events-none absolute inset-0" aria-hidden>
                {/* Aperture / lens — top left */}
                <svg className="absolute left-[3%] top-[8%] opacity-[0.06] sm:opacity-[0.09]" style={{ width: "clamp(90px, 12vw, 170px)" }} viewBox="0 0 100 100" fill="none">
                  <circle cx="50" cy="50" r="44" stroke="#f0abfc" strokeWidth="1.3" />
                  <circle cx="50" cy="50" r="28" stroke="#f0abfc" strokeWidth="1" opacity="0.6" />
                  <circle cx="50" cy="50" r="12" stroke="#f0abfc" strokeWidth="1" opacity="0.3" />
                  <line x1="50" y1="6" x2="50" y2="22" stroke="#f0abfc" strokeWidth="0.8" opacity="0.3" />
                  <line x1="50" y1="78" x2="50" y2="94" stroke="#f0abfc" strokeWidth="0.8" opacity="0.3" />
                  <line x1="6" y1="50" x2="22" y2="50" stroke="#f0abfc" strokeWidth="0.8" opacity="0.3" />
                  <line x1="78" y1="50" x2="94" y2="50" stroke="#f0abfc" strokeWidth="0.8" opacity="0.3" />
                </svg>
                {/* Sliders — bottom right */}
                <svg className="absolute bottom-[10%] right-[4%] opacity-[0.06] sm:opacity-[0.09]" style={{ width: "clamp(80px, 11vw, 160px)" }} viewBox="0 0 100 80" fill="none">
                  <line x1="10" y1="15" x2="90" y2="15" stroke="#c084fc" strokeWidth="1.2" opacity="0.4" />
                  <circle cx="35" cy="15" r="5" stroke="#c084fc" strokeWidth="1.3" />
                  <line x1="10" y1="40" x2="90" y2="40" stroke="#c084fc" strokeWidth="1.2" opacity="0.4" />
                  <circle cx="62" cy="40" r="5" stroke="#c084fc" strokeWidth="1.3" />
                  <line x1="10" y1="65" x2="90" y2="65" stroke="#c084fc" strokeWidth="1.2" opacity="0.4" />
                  <circle cx="48" cy="65" r="5" stroke="#c084fc" strokeWidth="1.3" />
                </svg>
                {/* Sparkle dots — top right */}
                <svg className="absolute right-[8%] top-[15%] opacity-[0.06] sm:opacity-[0.09]" style={{ width: "clamp(60px, 8vw, 110px)" }} viewBox="0 0 60 60" fill="none">
                  <circle cx="30" cy="10" r="2.5" fill="#e879f9" opacity="0.5" />
                  <circle cx="10" cy="35" r="1.5" fill="#e879f9" opacity="0.3" />
                  <circle cx="50" cy="30" r="3" fill="#e879f9" opacity="0.4" />
                  <circle cx="25" cy="50" r="2" fill="#e879f9" opacity="0.35" />
                  <circle cx="45" cy="55" r="1.5" fill="#e879f9" opacity="0.25" />
                  <path d="M30 2 L32 8 L38 10 L32 12 L30 18 L28 12 L22 10 L28 8 Z" stroke="#e879f9" strokeWidth="0.8" opacity="0.4" fill="none" />
                </svg>
              </div>
              <div className="relative mx-auto flex max-w-4xl flex-col-reverse gap-6 px-6 py-20 sm:flex-row sm:items-center sm:gap-14 sm:py-28">
                <div className="flex shrink-0 items-center justify-center">
                  <EffectsShowcase />
                </div>
                <div className="flex-1">
                  <h2 className="font-display text-[clamp(1.6rem,5vw,2.4rem)] font-extrabold leading-[1.05] tracking-[-0.04em] text-white">
                    {t("home.marketing.look.title").split("\n").map((line, index) => (
                      <React.Fragment key={line}>
                        {index > 0 ? <br /> : null}
                        {line}
                      </React.Fragment>
                    ))}
                  </h2>
                  <p className="mt-3 text-[0.9rem] leading-relaxed text-violet-300/50">
                    {t("home.marketing.look.body")}
                  </p>
                </div>
              </div>
            </section>

            <section className="relative z-[1] overflow-hidden bg-[#0f0720]">
              <div className="pointer-events-none absolute inset-0" aria-hidden>
                {/* Shield / gate — top right */}
                <svg className="absolute right-[5%] top-[8%] opacity-[0.06] sm:opacity-[0.09]" style={{ width: "clamp(80px, 11vw, 150px)" }} viewBox="0 0 80 100" fill="none">
                  <path d="M40 4 L72 20 L72 52 Q72 80 40 96 Q8 80 8 52 L8 20 Z" stroke="#fbbf24" strokeWidth="1.3" />
                  <path d="M40 24 L56 32 L56 48 Q56 64 40 72 Q24 64 24 48 L24 32 Z" stroke="#fbbf24" strokeWidth="1" opacity="0.4" />
                </svg>
                {/* Crossed out user — bottom left */}
                <svg className="absolute bottom-[10%] left-[4%] opacity-[0.06] sm:opacity-[0.09]" style={{ width: "clamp(70px, 9vw, 130px)" }} viewBox="0 0 80 80" fill="none">
                  <circle cx="40" cy="28" r="12" stroke="#f59e0b" strokeWidth="1.3" />
                  <path d="M20 68 Q20 50 40 50 Q60 50 60 68" stroke="#f59e0b" strokeWidth="1.3" />
                  <line x1="14" y1="14" x2="66" y2="66" stroke="#f59e0b" strokeWidth="1.5" strokeLinecap="round" opacity="0.5" />
                </svg>
                {/* Mute icon — bottom right */}
                <svg className="absolute bottom-[15%] right-[8%] opacity-[0.06] sm:opacity-[0.09]" style={{ width: "clamp(50px, 7vw, 90px)" }} viewBox="0 0 60 60" fill="none">
                  <rect x="22" y="8" width="16" height="28" rx="8" stroke="#fbbf24" strokeWidth="1.3" />
                  <path d="M14 30 Q14 48 30 48 Q46 48 46 30" stroke="#fbbf24" strokeWidth="1.2" opacity="0.5" />
                  <line x1="30" y1="48" x2="30" y2="56" stroke="#fbbf24" strokeWidth="1.2" opacity="0.4" />
                  <line x1="10" y1="10" x2="50" y2="50" stroke="#fbbf24" strokeWidth="1.3" strokeLinecap="round" opacity="0.4" />
                </svg>
              </div>
              <div className="relative mx-auto flex max-w-4xl flex-col gap-6 px-6 py-20 sm:flex-row sm:items-center sm:gap-14 sm:py-28">
                <div className="flex-1">
                  <h2 className="font-display text-[clamp(1.6rem,5vw,2.4rem)] font-extrabold leading-[1.05] tracking-[-0.04em] text-white">
                    {t("home.marketing.host.title").split("\n").map((line, index) => (
                      <React.Fragment key={line}>
                        {index > 0 ? <br /> : null}
                        {line}
                      </React.Fragment>
                    ))}
                  </h2>
                  <p className="mt-3 text-[0.9rem] leading-relaxed text-violet-300/50">
                    {t("home.marketing.host.body")}
                  </p>
                </div>
                <div className="flex shrink-0 items-center justify-center sm:w-64">
                  <div className="w-full max-w-[16rem] overflow-hidden rounded-2xl border border-amber-400/10 bg-amber-400/[0.03]">
                    {/* Lock header */}
                    <div className="flex items-center gap-2 border-b border-amber-400/8 px-4 py-2.5">
                      <LockClosedIcon className="h-3.5 w-3.5 text-amber-400/40" />
                      <span className="text-[10px] font-bold uppercase tracking-wider text-amber-300/40">{t("home.marketing.host.locked")}</span>
                    </div>
                    {/* Waiting request */}
                    <div className="px-4 py-3">
                      <div className="mb-2 text-[9px] font-bold uppercase tracking-wider text-amber-300/25">{t("home.marketing.host.waiting_to_join")}</div>
                      <div className="flex items-center gap-2.5">
                        <span className="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-amber-400/10 text-[10px] font-bold text-amber-300/50">SK</span>
                        <div className="min-w-0 flex-1">
                          <div className="text-[11px] font-semibold text-amber-100/60">Sara K.</div>
                          <div className="text-[9px] text-amber-300/30">{t("home.marketing.host.wants_to_join")}</div>
                        </div>
                        <div className="flex items-center gap-1.5">
                          <span className="grid h-6 w-6 place-items-center rounded-full bg-emerald-500/15 text-emerald-400/70">
                            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
                          </span>
                          <span className="grid h-6 w-6 place-items-center rounded-full bg-red-500/15 text-red-400/60">
                            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
                          </span>
                        </div>
                      </div>
                      {/* Second request, faded */}
                      <div className="mt-2.5 flex items-center gap-2.5 opacity-50">
                        <span className="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-amber-400/10 text-[10px] font-bold text-amber-300/50">JM</span>
                        <div className="min-w-0 flex-1">
                          <div className="text-[11px] font-semibold text-amber-100/60">James M.</div>
                          <div className="text-[9px] text-amber-300/30">{t("home.marketing.host.wants_to_join")}</div>
                        </div>
                        <div className="flex items-center gap-1.5">
                          <span className="grid h-6 w-6 place-items-center rounded-full bg-emerald-500/15 text-emerald-400/70">
                            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
                          </span>
                          <span className="grid h-6 w-6 place-items-center rounded-full bg-red-500/15 text-red-400/60">
                            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            <section className="relative z-[1] overflow-hidden bg-[#130a22]">
              {/* Decorative SVG illustrations */}
              <div className="pointer-events-none absolute inset-0" aria-hidden>
                {/* Video grid mockup — top left */}
                <svg className="absolute left-[3%] top-[8%] opacity-[0.06] sm:opacity-[0.09]" style={{ width: "clamp(140px, 18vw, 260px)" }} viewBox="0 0 180 130" fill="none">
                  <rect x="2" y="2" width="82" height="58" rx="10" stroke="#a78bfa" strokeWidth="1.5" />
                  <rect x="96" y="2" width="82" height="58" rx="10" stroke="#a78bfa" strokeWidth="1.5" />
                  <rect x="2" y="70" width="82" height="58" rx="10" stroke="#a78bfa" strokeWidth="1.5" />
                  <rect x="96" y="70" width="82" height="58" rx="10" stroke="#a78bfa" strokeWidth="1.5" />
                  <circle cx="43" cy="27" r="8" stroke="#a78bfa" strokeWidth="1.2" />
                  <circle cx="137" cy="27" r="8" stroke="#a78bfa" strokeWidth="1.2" />
                  <circle cx="43" cy="95" r="8" stroke="#a78bfa" strokeWidth="1.2" />
                  <circle cx="137" cy="95" r="8" stroke="#a78bfa" strokeWidth="1.2" />
                </svg>
                {/* Chat bubbles — top right */}
                <svg className="absolute right-[3%] top-[10%] opacity-[0.06] sm:opacity-[0.09]" style={{ width: "clamp(120px, 15vw, 220px)" }} viewBox="0 0 140 100" fill="none">
                  <rect x="30" y="2" width="108" height="32" rx="12" stroke="#c084fc" strokeWidth="1.5" />
                  <rect x="2" y="42" width="90" height="32" rx="12" stroke="#c084fc" strokeWidth="1.5" />
                  <circle cx="40" cy="18" r="4" fill="#c084fc" opacity="0.3" />
                  <line x1="54" y1="14" x2="120" y2="14" stroke="#c084fc" strokeWidth="1.2" opacity="0.25" />
                  <line x1="54" y1="22" x2="100" y2="22" stroke="#c084fc" strokeWidth="1.2" opacity="0.15" />
                  <circle cx="14" cy="58" r="4" fill="#c084fc" opacity="0.3" />
                  <line x1="26" y1="54" x2="76" y2="54" stroke="#c084fc" strokeWidth="1.2" opacity="0.25" />
                  <line x1="26" y1="62" x2="60" y2="62" stroke="#c084fc" strokeWidth="1.2" opacity="0.15" />
                </svg>
                {/* Waveform — bottom left */}
                <svg className="absolute bottom-[12%] left-[5%] opacity-[0.06] sm:opacity-[0.09]" style={{ width: "clamp(130px, 16vw, 240px)" }} viewBox="0 0 160 50" fill="none">
                  <polyline points="0,25 12,18 24,30 36,10 48,35 60,8 72,40 84,12 96,38 108,15 120,32 132,20 144,28 160,25" stroke="#818cf8" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
                {/* Play button — bottom right */}
                <svg className="absolute bottom-[10%] right-[5%] opacity-[0.06] sm:opacity-[0.09]" style={{ width: "clamp(70px, 9vw, 120px)" }} viewBox="0 0 80 80" fill="none">
                  <circle cx="40" cy="40" r="36" stroke="#f0abfc" strokeWidth="1.5" />
                  <polygon points="32,22 32,58 60,40" stroke="#f0abfc" strokeWidth="1.5" strokeLinejoin="round" fill="none" />
                </svg>
              </div>

              <div className="relative mx-auto max-w-4xl px-6 py-20 sm:py-28">
                <h2 className="font-display text-center text-[clamp(1.6rem,5vw,2.4rem)] font-extrabold leading-[1.05] tracking-[-0.04em] text-white">
                  {t("home.marketing.anything.title")}
                </h2>
                <p className="mx-auto mt-3 max-w-md text-center text-[0.9rem] leading-relaxed text-violet-300/50">
                  {t("home.marketing.anything.body")}
                </p>
                <div className="mt-10 flex flex-wrap items-center justify-center gap-3">
                  {marketingUseCases.map((use) => (
                    <span key={use} className="rounded-full border border-violet-400/10 bg-violet-400/5 px-4 py-2 text-sm font-semibold text-violet-300/60">
                      {use}
                    </span>
                  ))}
                </div>
              </div>
            </section>
      </>

      <footer className="relative z-[1] shrink-0 bg-[#0a0515] py-6 text-center text-xs text-violet-400/30">
        {t("footer.copyright")} &middot;{" "}
        <a href={termsHref} className="underline decoration-violet-400/20 transition hover:text-violet-300/50" target="_blank" rel="noopener noreferrer">
          {t("footer.terms")}
        </a>
      </footer>

      <ProfileSheet
        open={profileSheetOpen}
        initialProfile={profile}
        avatarUrl={session?.user?.avatar_url || ""}
        hasExistingSession={Boolean(session?.api_token)}
        saving={profileSheetSaving}
        errorMessage={profileSheetError}
        onSave={handleSaveProfile}
        onAvatarUpload={handleProfileAvatarUpload}
        onAvatarCropUpdate={handleProfileAvatarCropUpdate}
        onAvatarRemove={handleProfileAvatarRemove}
        onClose={closeProfileSheet}
        onClearError={() => setProfileSheetError("")}
      />
    </main>
  )
}
